import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'core/store';
import { InventoryItem, ProductGroupType } from 'core/types';
import {
  fetchProductTypeGroups,
  searchManufacturers
} from 'features/shared/inventory/store/InventoryActions';
import { resetManufacturers } from 'features/shared/inventory/store/InventorySlice';

// Stable reference for the pre-migration-fallback case below — `|| []` with a
// literal would create a new array every render, making the effects that
// depend on `productTypeGroupsList` below re-fire on every render for any
// browser with an old persisted `inventory` slice missing this field.
const EMPTY_PRODUCT_TYPE_GROUPS: ProductGroupType[] = [];

export interface SelectedCatalogModel {
  id: number;
  label: string;
}

/**
 * Sentinel value for the hardcoded "N/A" Brand/Manufacturer option — not a
 * real `manufacturers.id` (those are positive integers from the catalog).
 * This value itself is UI-only and never sent to the API; it just clears and
 * disables Model # so the homeowner can back out of the Brand step without
 * picking a real manufacturer. On Save, choosing it *does* persist — see
 * `saveEdits`/`save`'s `manufacturer_id: DEFAULT_MANUFACTURER_ID` branch,
 * which sends the API's real reserved "Generic" manufacturer id instead of
 * this sentinel.
 */
export const NA_MANUFACTURER_ID = -1;

/**
 * The API's reserved "Generic" manufacturer id (`site_constants.DEFAULT_MANUFACTURER_ID`
 * in avid-homebook-api) — sent as `manufacturer_id` when "N/A" is selected and saved.
 * The API special-cases this value in `_resolve_manufacturer_only_template` to resolve
 * via the one fully-generic template per Product Type, not a per-brand row.
 */
export const DEFAULT_MANUFACTURER_ID = 1;

/**
 * Shared Group -> Product Type -> Brand/Manufacturer -> Model cascade for the
 * item-detail edit forms (desktop inline + mobile InventoryProductEditForm),
 * per design.md D1/D6. Selecting a field clears everything below it,
 * top-down, mirroring InventoryAddProduct.tsx's existing Group->Type clear
 * pattern. Group/Type options come from the existing homeowner-safe
 * `productTypeGroupsList` (no new endpoint); Brand options come from the new
 * `searchManufacturers` thunk, scoped by the selected Product Type.
 *
 * `initialize(product)` is an explicit, imperative reset — called from each
 * form's "start editing" handler — rather than an auto-effect, so re-opening
 * edit mode always resets to the product's current values, matching the
 * existing `startEditing`/`editTemplate` imperative pattern in both forms.
 */
export const useProductCatalogEditFields = () => {
  const dispatch = useDispatch();
  const inventoryState = useSelector((state: RootState) => state.inventory);
  // `manufacturers`/`manufacturersLoading` are new slice fields — a browser
  // with an older persisted `inventory` slice (redux-persist, no migration
  // config in store.ts) rehydrates without them, so default defensively
  // rather than assuming redux-persist's shallow merge backfills new keys.
  const productTypeGroupsList =
    inventoryState.productTypeGroupsList || EMPTY_PRODUCT_TYPE_GROUPS;
  const manufacturers = inventoryState.manufacturers || [];
  const manufacturersLoading = inventoryState.manufacturersLoading || false;

  const [groupId, setGroupIdState] = useState<number | null>(null);
  const [typeId, setTypeIdState] = useState<number | null>(null);
  const [manufacturerId, setManufacturerIdState] = useState<number | null>(
    null
  );
  const [model, setModelState] = useState<SelectedCatalogModel | null>(null);

  useEffect(() => {
    if (!productTypeGroupsList.length) {
      dispatch(fetchProductTypeGroups());
    }
  }, [dispatch, productTypeGroupsList]);

  useEffect(() => {
    if (typeId) {
      dispatch(searchManufacturers(typeId));
    } else {
      dispatch(resetManufacturers());
    }
  }, [dispatch, typeId]);

  // Reconciles groupId once productTypeGroupsList finishes loading, covering
  // the case where `initialize()` ran before the list (fetched by the effect
  // above) had resolved — typeId is already known, groupId just needs the
  // list to look it up in.
  useEffect(() => {
    if (groupId !== null || typeId === null || !productTypeGroupsList.length) {
      return;
    }
    const matchedGroup = productTypeGroupsList.find((group) =>
      group.product_template_types.some((type) => type.id === typeId)
    );
    if (matchedGroup) {
      setGroupIdState(matchedGroup.id);
    }
  }, [productTypeGroupsList, typeId, groupId]);

  const initialize = (product: InventoryItem) => {
    const currentTypeId = product.product_template.type_id;
    const matchedGroup = productTypeGroupsList.find((group) =>
      group.product_template_types.some((type) => type.id === currentTypeId)
    );
    setGroupIdState(matchedGroup ? matchedGroup.id : null);
    setTypeIdState(currentTypeId);
    // A product currently pointed at the fully-generic template
    // (manufacturer_id === DEFAULT_MANUFACTURER_ID, set when "N/A" was saved)
    // re-opens showing "N/A" selected, not that manufacturer's literal name —
    // it isn't one of the real, selectable manufacturerOptions anyway.
    const currentManufacturerId =
      product.product_template.manufacturer?.id ?? null;
    setManufacturerIdState(
      currentManufacturerId === DEFAULT_MANUFACTURER_ID
        ? NA_MANUFACTURER_ID
        : currentManufacturerId
    );
    setModelState({
      id: product.product_template.id,
      label: product.product_template.model
    });
  };

  const setGroupId = (nextGroupId: number | null) => {
    setGroupIdState(nextGroupId);
    setTypeIdState(null);
    setManufacturerIdState(null);
    setModelState(null);
  };

  const setTypeId = (nextTypeId: number | null) => {
    setTypeIdState(nextTypeId);
    setManufacturerIdState(null);
    setModelState(null);
  };

  const setManufacturerId = (nextManufacturerId: number | null) => {
    setManufacturerIdState(nextManufacturerId);
    setModelState(null);
  };

  const setModel = (nextModel: SelectedCatalogModel | null) => {
    setModelState(nextModel);
  };

  const groupOptions = useMemo(
    () =>
      productTypeGroupsList.map((group) => ({
        value: group.id,
        label: group.name
      })),
    [productTypeGroupsList]
  );

  const typeOptions = useMemo(() => {
    const group = productTypeGroupsList.find((g) => g.id === groupId);
    return group
      ? group.product_template_types.map((type) => ({
          value: type.id,
          label: type.label
        }))
      : [];
  }, [productTypeGroupsList, groupId]);

  const manufacturerOptions = useMemo(
    () => [
      { value: NA_MANUFACTURER_ID, label: 'N/A' },
      ...manufacturers.map((manufacturer) => ({
        value: manufacturer.id,
        label: manufacturer.name
      }))
    ],
    [manufacturers]
  );

  return {
    initialize,
    groupId,
    setGroupId,
    groupOptions,
    typeId,
    setTypeId,
    typeOptions,
    manufacturerId,
    setManufacturerId,
    manufacturerOptions,
    manufacturersLoading,
    model,
    setModel
  };
};
